import { apiClient } from '../config'

export type DynamicQuestionResponse = {
  q: string
  a: string
  answeredAt: string
}

export type ProfileResponse = {
  id: string
  age?: string
  sex?: 'male' | 'female' | 'other'
  weight?: string
  height?: string
  bloodPressureSystolic?: string
  bloodPressureDiastolic?: string
  healthGoals?: string[]
  healthConcerns?: string[]
  medications?: string
  supplements?: string
  familyHistory?: string
  activeSymptoms?: string
  previousLabs?: string
  testsOfInterest?: string
  insurance?: string
  smoking?: 'yes' | 'no' | 'unknown'
  focusType?: 'health' | 'symptoms'
  dynamicQuestions?: DynamicQuestionResponse[]
  createdAt: string
  updatedAt: string
}

export type ProfileWithValidationResponse = {
  profile: ProfileResponse
  missingRequiredFields: string[]
}

export type AIQuestion = {
  i: string        // Question ID
  t: string        // Question text/title
  y: 0 | 1         // Question type: 0=select, 1=multiSelect
  o: string[]      // Options array
  c: string        // Icon name
}

export type AIQuestionsMetadata = {
  modelUsed: string
  tokensUsed?: number
  processingTimeMs?: number
  prompt?: string
  promptHash?: string
  responseType?: string
  status?: string
  createdAt?: string
}

export type AIQuestionsResponse = {
  sessionId: string
  questions: AIQuestion[]
  metadata?: AIQuestionsMetadata
}

export type PatientProfileExportRecord = {
  patientLabel: string
  userId: string
  age: string | null
  sex: 'male' | 'female' | 'other' | null
  weight: string | null
  height: string | null
  bloodPressureSystolic: string | null
  bloodPressureDiastolic: string | null
  healthGoals: string[]
  healthConcerns: string[]
  smoking: 'yes' | 'no' | 'unknown' | null
  focusType: 'health' | 'symptoms' | null
  medications: string | null
  supplements: string | null
  familyHistory: string | null
  activeSymptoms: string | null
  previousLabs: string | null
  testsOfInterest: string | null
  insurance: string | null
}

export const profilesService = {
  getUserProfile: async (userId: string): Promise<ProfileWithValidationResponse | null> => {
    const response = await apiClient.get<ProfileWithValidationResponse | null>(`/onboarding/profiles/user/${userId}`)
    return response.data
  },

  generateQuestionsForUser: async (userId: string): Promise<AIQuestionsResponse> => {
    const response = await apiClient.post<AIQuestionsResponse>(`/onboarding/profiles/user/${userId}/generate-questions`)
    return response.data
  },

  getQuestionsForUser: async (userId: string): Promise<AIQuestionsResponse> => {
    const response = await apiClient.get<AIQuestionsResponse>(`/onboarding/profiles/user/${userId}/questions`)
    return response.data
  },

  getFilledPatientProfilesForExport: async (): Promise<PatientProfileExportRecord[]> => {
    const response = await apiClient.get<PatientProfileExportRecord[]>('/onboarding/profiles/export/patient-profiles')
    return response.data
  },
}